import { useAppDispatch, useAppSelector } from "@/shared/lib/redux"
import { Modal } from "@/shared/ui/modal"
import { yupResolver } from "@hookform/resolvers/yup"
import { SubmitHandler, useForm } from "react-hook-form"
import { FormInput } from "@/shared/ui/form-input"
import notify from "@/shared/lib/notify"
import { getErrorMessage } from "@/shared/consts/error-codes"
import { closeChangeUserGroupModal, fetchChangeUserGroup, selectIsChangeUserGroupModalOpen, selectOnSuccess, changeUserGroupValidationSchema, selectCurrentUser } from "../../model"
import { useMemo, useEffect } from "react"
import { selectUsersGroups, selectEmployeesGroups, fetchUsersGroups, fetchEmployeesGroups } from "@/entities/groups"

interface ChangeUserGroupFormValues {
    group_id: string | null
}

const initialState: ChangeUserGroupFormValues = {
    group_id: null
}

export const ChangeUserGroupModal = () => {
    const dispatch = useAppDispatch()
    const isOpen = useAppSelector(selectIsChangeUserGroupModalOpen)
    const onSuccess = useAppSelector(selectOnSuccess)
    const currentUser = useAppSelector(selectCurrentUser)
    const usersGroups = useAppSelector(selectUsersGroups)
    const employeesGroups = useAppSelector(selectEmployeesGroups)

    useEffect(() => {
        if (isOpen) {
            if (usersGroups.length === 0) {
                dispatch(fetchUsersGroups())
            }
            if (employeesGroups.length === 0) {
                dispatch(fetchEmployeesGroups())
            }
        }
    }, [isOpen, usersGroups.length, employeesGroups.length, dispatch])

    const groupSelectOptions = useMemo(() => {
        const options = [
            { value: null, label: 'Без группы' },
            ...usersGroups.map((group) => ({
                value: group.id,
                label: group.name
            })),
            ...employeesGroups.map((group) => ({
                value: group.id,
                label: group.name
            }))
        ]
        return options
    }, [usersGroups, employeesGroups])

    const handleClose = () => {
        dispatch(closeChangeUserGroupModal())
    }

    const onSubmit: SubmitHandler<typeof initialState> = async (data) => {
        await new Promise((res, rej) => {
            dispatch(fetchChangeUserGroup({
                groupId: data.group_id
            }))
                .unwrap()
                .then(() => {
                    const message = data.group_id === null 
                        ? 'Пользователь успешно удален из группы'
                        : 'Группа пользователя успешно изменена'
                    notify.success(message)
                    reset()
                    const selectedGroup = groupSelectOptions.find((option) => option.value === data.group_id)
                    onSuccess?.(selectedGroup ? {
                        id: selectedGroup.value,
                        name: selectedGroup.value ? selectedGroup.label : null
                    } : null)
                    handleClose()
                    res('')
                })
                .catch((error) => {
                    notify.error(getErrorMessage(error))
                    rej()
                })
        })
    }

    const {
        handleSubmit,
        control,
        formState: { errors, isSubmitting },
        reset
    } = useForm({
        defaultValues: {
            group_id: currentUser.group_id || null
        },
        resolver: yupResolver(changeUserGroupValidationSchema)
    })

    useEffect(() => {
        if (isOpen) {
            reset({
                group_id: currentUser.group_id || null
            })
        }
    }, [isOpen, currentUser.group_id, reset])

    return (
        <Modal
            isOpen={isOpen}
            title="Изменение группы пользователя"
            onRequestClose={handleClose}
        >
            <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-3">
                <FormInput
                    label={"Группа"}
                    id={"group_id"}
                    options={groupSelectOptions}
                    control={control}
                    error={errors['group_id']}
                    type="select"
                    required={false}
                    placeholder="Выберите группу"
                />
                <div className="w-full flex gap-3">
                    <button onClick={handleClose} className="form_cancel_button" type="button">
                        Отменить
                    </button>
                    <button disabled={isSubmitting} className="form_handle_button" type="submit">
                        Изменить
                    </button>
                </div>
            </form>
        </Modal>
    )
}

